

export enum BasinQvsiProperties {
    basin = "Basin",
    qvsiGroup = "Q vs I Group",
    mode = 'Mode',
    useAlternate = 'Use Alternate Regression',
    type = 'Type',
    n = 'n',
    r2 = 'R²',
    slope = 'Slope',
    yIntercept = 'Y-Intercept',
    area = 'Area',
    sewerLength = 'Sewer Length',
    periodType = 'Period Type'
}

const BasinQvsiNumberProperties = ['n', 'R2', 'Slope', 'Y-Intercept'];

export const AREA = 'Area';
export const SEWER_LENGTH = 'Sewer Length';
const PERIOD_TYPE = 'Period Type';

export const PEAK_II = 'Peak I/I';
export const ROLLING_PEAK_RAINFALL = 'Rolling Peak Rainfall Intensity';
export const RAINFAILL_TIME__PEAK = 'Rainfall to Time of Peak I/I';
export const STORM_PERIOD = 'Storm Period RDII';
export const RAINFALL_DEPTH = 'Rainfall Depth';
export const TOTAL_EVENT_RDII = 'Total Event RDII';

export const mapTypeToString = (type: string): string => {
    switch (type) {
        case 'PeakPeak':
            return 'Peak I/I vs. Rolling Peak Rainfall Intensity';
        case 'PeakVolToPeak':
            return 'Peak I/I vs. Rainfall to Time of Peak I/I';
        case 'VolumeStorm':
            return 'Storm Period RDII vs. Rainfall Depth';
        case 'VolVol':
            return 'Total Event RDII vs. Rainfall Depth';
        default:
            return type;
    }
};

export const PROJECTED = 'Projected'
export const YVALUE_FIELD_SUFIX = "Y-Value";
export const PROJECTED_NET_II = 'Projected NetII';
export const PROJECTED_NET_II_SPACED = 'Projected Net II';
export const STORM_NET_II_COLUMN = 'STORM NET II';
export const EVENT_NET_II_COLUMN = 'Event Net II';
export const R_VALUE = 'R-Value';
const isProjectedYValue = (key: string) => key.endsWith(YVALUE_FIELD_SUFIX);
const isProjectedNetII = (key: string) => key.toLowerCase().includes(PROJECTED_NET_II_SPACED.toLowerCase());
const isRValue = (key: string) => key.includes(R_VALUE);

const isArea = (key: string) => key.includes(AREA);
const isSewLen = (key: string) => key.includes(SEWER_LENGTH);
const isPeriodType = (key: string) => key.includes(PERIOD_TYPE);

export type CsvParsedRow = Record<string, string | number | null>;

export const csvToJSON = (text: string, quoteChar = '"', delimiter = ','): CsvParsedRow[] => {
    const rows = text.split("\n");

    // Parse headers respecting quoted fields
    const parseCSVLine = (line: string): string[] => {
        const result: string[] = [];
        let current = '';
        let inQuotes = false;

        for (let i = 0; i < line.length; i++) {
            const char = line[i];

            if (char === quoteChar) {
                // Check for escaped quotes (double quotes)
                if (inQuotes && line[i + 1] === quoteChar) {
                    current += quoteChar;
                    i++; // Skip next quote
                } else {
                    inQuotes = !inQuotes;
                }
            } else if (char === delimiter && !inQuotes) {
                result.push(current);
                current = '';
            } else {
                current += char;
            }
        }
        // Push the last field
        if(current)
            result.push(current);
        return result;
    };

    const headers = parseCSVLine(rows[0]).map(h => h.trim());
    const lines = rows.slice(1);

    return lines.map(line => {
        const values = parseCSVLine(line);
        return values.reduce<CsvParsedRow>((acc, cur, i) => {
            // replace blank matches with `null`
            const val = cur.length <= 0 ? null : Number(cur) || cur;
            const key = headers[i] ?? `{i}`;
            return { ...acc, [key]: val };
        }, {});
    });
}

export const createPivotMapping = (data: Record<string, unknown>): { [key: string]: { type: string; format?: string; interval?: string } } => {

    return Object.keys(data).reduce((result, key) => {
        const value = data[key];

        const isNumber = isArea(key) || isSewLen(key) || isProjectedYValue(key) || isProjectedNetII(key) || BasinQvsiNumberProperties.includes(key);

        const type = isNumber ? 'number' : 'string';
        result[key] = { type };

        return result;
    }, {});
}

export const getBasinQvsiPivotOptions = () => {
    return {
        configuratorButton: false,
        showAggregationLabels: false,
        showAggregations: false,
        expandAll: true,
        grid: {
            type: "flat",
            showEmptyValues: true,  
            showGrandTotals: "off",
            showTotals: "off",
        },
        chart: {
            multipleMeasures: true,
        },
    }
}

export const getExistingProps = (props: BasinQvsiProperties[], item) => {
    return props.filter(v => getKeyByProp(item, v));
}


export const getKeyByProp = (basinQvsi, prop: BasinQvsiProperties) => {
    return Object.keys(basinQvsi).find(v => v.includes(prop));
}


const BASIN_QVSI_SLICE_ROWS = [
    BasinQvsiProperties.basin
];

const BASIN_QVSI_COLUMNS = [];

export const BASIN_QVSI_MEASURES = [
    BasinQvsiProperties.n,
    BasinQvsiProperties.r2,
];

export const getBasinQvsiPivotSlice = (basinQvsi, isMetric = false) => {
	return {
		rows: [
			{ uniqueName: "qvsiGroup", caption: BasinQvsiProperties.qvsiGroup },
			{ uniqueName: "mode", caption: BasinQvsiProperties.mode },
			{ uniqueName: "useAlt", caption: BasinQvsiProperties.useAlternate },
			{ uniqueName: "type", caption: BasinQvsiProperties.type }
		],
		columns: [{uniqueName: "[Measures]"}],
		measures: getBasinQvsIPivotMeasures(basinQvsi, isMetric),
		expandAll: true
	};
}

export const getResultsTabPivotSlice = (basinQvsi, isMetric = false, isCfs = false) => {
	const dynamicColumns = [];
	const nKey = Object.keys(basinQvsi).find(key => key.toLowerCase() === 'n');
	if (nKey) {
		dynamicColumns.push({ uniqueName: nKey });
	}

	return {
		rows: BASIN_QVSI_SLICE_ROWS.map(prop => ({ uniqueName: getKeyByProp(basinQvsi, prop) })),
		columns: [{uniqueName: "[Measures]"}, ...dynamicColumns],
		measures: getResultsTabPivotMeasures(basinQvsi, isMetric, isCfs),
		expandAll: true
	};
}

export const getResultsTabPivotMeasures = (basinQvsi, isMetric = false, isCfs = false) => {
	const designedStorms = [];
	let additionalFields = [];
	let hasDesignStorms = false;
	const projectedNetIIFormat = isCfs ? "0_decimal_format" : isMetric ? "1_decimal_format" : "3_decimal_format";

	for (const key of Object.keys(basinQvsi)) {
		if (isProjectedYValue(key)) {
			hasDesignStorms = true;
			designedStorms.push({ uniqueName: key, format: "3_decimal_format" });
		} else if (isRValue(key)) {
			designedStorms.push({ uniqueName: key, format: "2_decimal_format" });
		} else if (isProjectedNetII(key)) {
			designedStorms.push({ uniqueName: key, format: projectedNetIIFormat });
		} else if (isArea(key)) {
			additionalFields.push({ uniqueName: key, format: "2_decimal_format" });
		} else if (isSewLen(key)) {
			additionalFields.push({ uniqueName: key, format: "2_decimal_format" });
		} else if (isPeriodType(key)) {
			additionalFields.push({ uniqueName: key });
		}
	}

	if (!hasDesignStorms) {
		additionalFields = additionalFields.filter(v => !v.uniqueName.includes(PERIOD_TYPE));
	}

	return [...BASIN_QVSI_MEASURES.map(prop => ({ uniqueName: getKeyByProp(basinQvsi, prop) })), ...additionalFields, ...designedStorms];
}

export const getBasinQvsIPivotMeasures = (basinQvsi, isMetric = false) => {
    const designedStorms = [];
    let additionalFields = [];

    let hasDesignStorms = false;

    const projectedNetIIFormat = isMetric ? "1_decimal_format" : "2_decimal_format";

    // Base measures - use actual property names
    const baseMeasures = [
        { uniqueName: "n", caption: BasinQvsiProperties.n, format: "0_decimal_format" },
        { uniqueName: "r2", caption: BasinQvsiProperties.r2, format: "2_decimal_format" },
        { uniqueName: "slope", caption: BasinQvsiProperties.slope, format: "4_decimal_format" },
        { uniqueName: "yIntercept", caption: BasinQvsiProperties.yIntercept, format: "3_decimal_format" }
    ];

    // Check for dynamic fields from the actual keys in basinQvsi
    for (const key of Object.keys(basinQvsi)) {
        if (isProjectedYValue(key)) {
            hasDesignStorms = true;
            designedStorms.push({ uniqueName: key, format: "3_decimal_format" });
        } else if (isRValue(key)) {
            designedStorms.push({ uniqueName: key, format: "2_decimal_format" });
        } else if (isProjectedNetII(key)) {
            designedStorms.push({ uniqueName: key, format: projectedNetIIFormat });
        } else if (isPeriodType(key)) {
            additionalFields.push({ uniqueName: key });
        }
    }

    // #42468 Period type should be displayed when no design storm
    if (!hasDesignStorms) {
        additionalFields = additionalFields.filter(v => !v.uniqueName.includes(PERIOD_TYPE));
    }

    return [...baseMeasures, ...additionalFields, ...designedStorms];
}
